import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { ALLERGENS_FRAGMENT, WEIGHT_FRAGMENT, FOOD_ENERGY_FRAGMENT } from "../../fragments/menuItem";

import type {
  MenuItemEditResp,
  MenuItemEditVariables,
  MenuItemVariables,
  MenuItem
} from "../../types/menuItemTypes";

const UPDATE_MENU_ITEM = gql`
  mutation UpdateMenuItem($attributes: UpdateMenuItemInput!) {
    updateMenuItem(attributes: $attributes) {
      menuItem {
        id
        menuItemLangs {
          id
          name
          description
          language
        }
        menuSectionIds
        vegan
        chili
        hot
        price
        img {
          small
          medium
        }
        updatedAt
        ...AllergensFragment
        ...WeightFragment
        ...FoodEnergyFragment
      }
      errors
    }
  }
  ${ALLERGENS_FRAGMENT}
  ${WEIGHT_FRAGMENT}
  ${FOOD_ENERGY_FRAGMENT}
`

type UseUpdateMenuItemOptions = {
  onCompleted?: (menuItem: MenuItem) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuItemEditResp, MenuItemEditVariables>>[1];
};

const useUpdateMenuItem = (options?: UseUpdateMenuItemOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuItemEditResp,
    MenuItemEditVariables
  >(UPDATE_MENU_ITEM, options?.mutationOptions);

  const updateMenuItem = useCallback(
    async (input: MenuItemVariables & { id: string }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id,
              sugarFree: input.sugarFree,
              glutenFree: input.glutenFree,
              cornFree: input.cornFree,
              dairyFree: input.dairyFree,
              eggsFree: input.eggsFree,
              fishFree: input.fishFree,
              legumesFree: input.legumesFree,
              peanutFree: input.peanutFree,
              seedsFree: input.seedsFree,
              shellfishFree: input.shellfishFree,
              treeNutsFree: input.treeNutsFree,
              vegan: input.vegan,
              chili: input.chili,
              hot: input.hot,
              amount: input.amount,
              unit: input.unit,
              mass: input.mass,
              amountInBase: input.amountInBase,
              price: input.price,
              proteins: input.proteins,
              fats: input.fats,
              carbohydrates: input.carbohydrates,
              calories: input.calories,
              image: input.image,
              menuItemLangs: input.menuItemLangs,
              menuSectionIds: input.menuSectionIds
            },
          },
        });

        const updateMenuItem = result?.data?.updateMenuItem;

        if (updateMenuItem?.errors?.length) {
          throw new Error(updateMenuItem.errors.join(", "));
        }

        if (options?.onCompleted && updateMenuItem?.menuItem) {
          options.onCompleted(updateMenuItem.menuItem);
        }

        return updateMenuItem?.menuItem;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const MenuItem = data?.updateMenuItem?.menuItem;

  return {
    updateMenuItem,
    updatedMenuItem: MenuItem,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateMenuItem
